/**
 * SysAdmin: enheter med aktiv GPS dump-kö.
 * Visar bara de som ligger efter; försvinner när de kommer ikapp.
 * Flush skickar Teltonika flush via Jasper SMS.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowPathIcon,
  ClockIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useTranslation } from '@/store/languageStore';
import { PageBackButton } from '@/components/common/PageBackButton';
import {
  gpsDataLagService,
  type GpsDataLagMeta,
  type GpsDataLagVehicle,
} from '@/services/sysadmin/gpsDataLagService';

const AUTO_REFRESH_MS = 60_000;

function formatWhen(iso: string | null): string {
  if (!iso) return '–';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '–';
  return new Intl.DateTimeFormat('sv-SE', {
    dateStyle: 'short',
    timeStyle: 'medium',
  }).format(d);
}

function formatLag(min: number | null): string {
  if (min == null || !Number.isFinite(min)) return '–';
  if (min < 60) return `${Math.round(min)} min`;
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  return m > 0 ? `${h} h ${m} min` : `${h} h`;
}

export default function GpsDataLagPage() {
  const { t } = useTranslation();
  const [vehicles, setVehicles] = useState<GpsDataLagVehicle[]>([]);
  const [meta, setMeta] = useState<GpsDataLagMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [flushingImei, setFlushingImei] = useState<string | null>(null);

  const load = useCallback(
    async (refresh = false) => {
      setLoading(true);
      try {
        const result = await gpsDataLagService.list({ min: 60, refresh });
        setVehicles(result.vehicles);
        setMeta(result.meta);
      } catch (e: unknown) {
        const err = e as { response?: { data?: { error?: string } } };
        toast.error(err?.response?.data?.error || t('sysAdmin.gpsDataLag.loadError'));
      } finally {
        setLoading(false);
      }
    },
    [t]
  );

  useEffect(() => {
    void load(true);
  }, [load]);

  useEffect(() => {
    const id = window.setInterval(() => {
      void load(true);
    }, AUTO_REFRESH_MS);
    return () => window.clearInterval(id);
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return vehicles;
    return vehicles.filter(
      (v) =>
        v.imei.toLowerCase().includes(q) ||
        (v.iccid || '').toLowerCase().includes(q) ||
        (v.registrationNumber || '').toLowerCase().includes(q) ||
        (v.organizationName || '').toLowerCase().includes(q)
    );
  }, [vehicles, search]);

  const onFlush = async (vehicle: GpsDataLagVehicle) => {
    const label = vehicle.registrationNumber || vehicle.imei;
    if (!window.confirm(t('sysAdmin.gpsDataLag.confirmFlush', { reg: label }))) {
      return;
    }
    setFlushingImei(vehicle.imei);
    try {
      await gpsDataLagService.flush(vehicle.imei);
      toast.success(t('sysAdmin.gpsDataLag.flushOk', { reg: label }));
      void load(true);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      toast.error(err?.response?.data?.error || t('sysAdmin.gpsDataLag.flushFail'));
    } finally {
      setFlushingImei(null);
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-4 max-w-7xl mx-auto">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <PageBackButton />
          <div>
            <div className="flex items-center gap-2">
              <ClockIcon className="w-7 h-7 text-primary shrink-0" />
              <h1 className="text-xl font-semibold text-textDefault">
                {t('sysAdmin.gpsDataLag.title')}
              </h1>
            </div>
            <p className="text-sm text-textSecondary mt-1 max-w-2xl">
              {t('sysAdmin.gpsDataLag.description')}
            </p>
            {meta && (
              <p className="text-xs text-textTertiary mt-1">
                {t('sysAdmin.gpsDataLag.summary', {
                  count: meta.dumpingCount ?? vehicles.length,
                  snapped: formatWhen(meta.snappedAt),
                })}
              </p>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={() => void load(true)}
          disabled={loading}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-border bg-bgElevated text-sm hover:bg-bgContainer disabled:opacity-50"
        >
          <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          {t('sysAdmin.gpsDataLag.refresh')}
        </button>
      </div>

      <div className="flex flex-wrap gap-3 items-center">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('sysAdmin.gpsDataLag.searchPlaceholder')}
          className="px-3 py-2 rounded-lg border border-border bg-bgPrimary text-sm min-w-[220px]"
        />
      </div>

      <div className="overflow-x-auto rounded-lg border border-border bg-bgElevated">
        <table className="w-full text-sm">
          <thead className="bg-bgContainer text-textSecondary">
            <tr>
              <th className="text-left px-3 py-2 font-medium">{t('sysAdmin.gpsDataLag.colReg')}</th>
              <th className="text-left px-3 py-2 font-medium">{t('sysAdmin.gpsDataLag.colOrg')}</th>
              <th className="text-left px-3 py-2 font-medium">{t('sysAdmin.gpsDataLag.colImei')}</th>
              <th className="text-left px-3 py-2 font-medium">{t('sysAdmin.gpsDataLag.colIccid')}</th>
              <th className="text-left px-3 py-2 font-medium">{t('sysAdmin.gpsDataLag.colDumpLag')}</th>
              <th className="text-left px-3 py-2 font-medium">{t('sysAdmin.gpsDataLag.colDeviceData')}</th>
              <th className="text-left px-3 py-2 font-medium">{t('sysAdmin.gpsDataLag.colReceived')}</th>
              <th className="text-left px-3 py-2 font-medium">{t('sysAdmin.gpsDataLag.colLastRecs')}</th>
              <th className="text-left px-3 py-2 font-medium">{t('sysAdmin.gpsDataLag.colActions')}</th>
            </tr>
          </thead>
          <tbody>
            {loading && vehicles.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-3 py-8 text-center text-textTertiary">
                  {t('sysAdmin.gpsDataLag.loading')}
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-3 py-8 text-center text-textTertiary">
                  {t('sysAdmin.gpsDataLag.empty')}
                </td>
              </tr>
            ) : (
              filtered.map((v) => (
                <tr
                  key={v.imei}
                  className="border-t border-border hover:bg-bgContainer/60 bg-red-50/40 dark:bg-red-950/20"
                >
                  <td className="px-3 py-2 font-medium whitespace-nowrap">
                    {v.registrationNumber || '–'}
                    {v.smsSentAt && (
                      <span className="ml-2 text-xs text-green-600 dark:text-green-400">
                        {t('sysAdmin.gpsDataLag.smsBadge')}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-textSecondary max-w-[180px] truncate">
                    {v.organizationName || '–'}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">{v.imei}</td>
                  <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">
                    {v.iccid || '–'}
                  </td>
                  <td className="px-3 py-2 font-medium text-red-600 dark:text-red-400 whitespace-nowrap">
                    {formatLag(v.dumpLagMinutes)}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-textSecondary">
                    {formatWhen(v.deviceDataAt)}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-textSecondary">
                    {formatWhen(v.receivedAt)}
                  </td>
                  <td className="px-3 py-2 text-xs text-textTertiary max-w-[140px] truncate">
                    {v.lastRecs || '–'}
                  </td>
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      onClick={() => void onFlush(v)}
                      disabled={flushingImei === v.imei}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-primary text-bgPrimary text-xs font-medium hover:opacity-90 disabled:opacity-50"
                      title={t('sysAdmin.gpsDataLag.flushTitle')}
                    >
                      <PaperAirplaneIcon className="w-3.5 h-3.5" />
                      {flushingImei === v.imei
                        ? t('sysAdmin.gpsDataLag.flushing')
                        : t('sysAdmin.gpsDataLag.flush')}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
